import axios from 'axios';

// URLs that should be excluded from automatic token refresh
const REFRESH_EXCEPTIONS = [
    '/notification/update/fcm/token'
];

// Safari mobile detection
const isSafariMobile = /iPhone|iPad/.test(navigator.userAgent) &&
    /Safari/.test(navigator.userAgent) &&
    !/Chrome|CriOS|FxiOS/.test(navigator.userAgent);

// Request queue for failed requests
let failedRequestsQueue = [];

// Create axios instance with default config
const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 60000
});

// Function to check if URL should be excluded from refresh
const shouldSkipRefresh = (url) => {
    return REFRESH_EXCEPTIONS.some(exception => url.includes(exception));
};

// Function to get the current access token
const getAccessToken = () => {
    return document.cookie
        .split('; ')
        .find(row => row.startsWith('access_token='))
        ?.split('=')[1];
};

const getRefreshToken = () => {
    return document.cookie
        .split('; ')
        .find(row => row.startsWith('refresh_token='))
        ?.split('=')[1];
};

// Function to clear authentication tokens
const clearAuthTokens = () => {
    document.cookie = 'access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = 'refresh_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
};

// Function to refresh the access token
const refreshAccessToken = async () => {
    try {
        let refreshToken = getRefreshToken();

        if (!refreshToken)
            refreshToken = '';

        const response = await fetch(process.env.REACT_APP_API_BASE_URL + '/users/refresh/access/token', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refresh_token: refreshToken })
        });
        
        if (!response.ok) {
            // For other HTTP errors (server down, etc.), don't clear tokens
            throw new Error('Token refresh failed');
        }

        const data = await response.json();

        if (!data?.status) {
            // If response is OK but data indicates failure, clear tokens
            clearAuthTokens();
            throw new Error('Refresh token expired');
        }

        // Update access token
        document.cookie = `access_token=${data.data.access_token}; path=/`;
        
        // Update refresh token if provided
        if (data.data.refresh_token) {
            document.cookie = `refresh_token=${data.data.refresh_token}; path=/`;
        }
        
        return data.data.access_token;
    } catch (error) {
        // For network errors or other exceptions, don't clear tokens
        // Only throw the original error without clearing tokens
        throw error;
    }
};

// Function to show alert for API failures
const showApiFailureAlert = (error, requestConfig) => {
    const statusCode = error.response?.status || 'N/A';
    const statusText = error.response?.statusText || 'N/A';
    const errorMessage = error.message || 'Unknown error';
    const errorCode = error.code || 'N/A';
    const url = requestConfig?.url || error.config?.url || error.request?.responseURL || 'Unknown URL';
    const method = requestConfig?.method?.toUpperCase() || error.config?.method?.toUpperCase() || 'N/A';
    
    // Build alert message
    let alertMessage = `API Request Failed\n\n`;
    alertMessage += `Method: ${method}\n`;
    alertMessage += `URL: ${url}\n`;
    alertMessage += `Status Code: ${statusCode}\n`;
    if (statusText !== 'N/A') {
        alertMessage += `Status Text: ${statusText}\n`;
    }
    if (errorCode !== 'N/A') {
        alertMessage += `Error Code: ${errorCode}\n`;
    }
    alertMessage += `Error Message: ${errorMessage}`;
    
    // Show response data if available
    if (error.response?.data) {
        const responseData = typeof error.response.data === 'object' 
            ? JSON.stringify(error.response.data, null, 2)
            : error.response.data;
        alertMessage += `\n\nResponse Data:\n${responseData}`;
    }
    
    alert(alertMessage);
    console.error('API Failure:', {
        method,
        url,
        statusCode,
        statusText,
        errorCode,
        errorMessage,
        responseData: error.response?.data,
        fullError: error
    });
};

// Function to retry failed requests when tab becomes active
const retryFailedRequests = async () => {
    if (failedRequestsQueue.length === 0) return;

    console.log(`Retrying ${failedRequestsQueue.length} failed requests...`);

    const requestsToRetry = [...failedRequestsQueue];
    failedRequestsQueue = [];

    for (const { config, resolve, reject } of requestsToRetry) {
        try {
            const response = await api(config);
            resolve(response);
        } catch (error) {
            reject(error);
        }
    }
};

// Add request interceptor to add Authorization header
api.interceptors.request.use(
    (config) => {
        const token = getAccessToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Enhanced response interceptor with Safari timeout handling
api.interceptors.response.use(
    async (response) => {
        // Check for error_code in response data
        const originalRequest = response.config;

        if (response.data?.error_code === 1006) {
            // Check if this URL should skip refresh
            if (shouldSkipRefresh(originalRequest.url)) {
                return response;
            }

            originalRequest._retry = true;
            try {
                const newToken = await refreshAccessToken();
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return api(originalRequest);
            } catch (refreshError) {
                return Promise.reject(refreshError);
            }
        }
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        // Don't show alerts for token refresh errors (handled internally)
        const isTokenRefreshError = originalRequest?.url?.includes('/users/refresh/access/token') || 
                                   originalRequest?.url?.includes('refresh/access/token');

        // Handle Safari mobile timeout errors
        if (error.code === 'ECONNABORTED' && isSafariMobile && originalRequest) {
            console.log('Safari timeout detected, preparing for retry...');

            // If tab is currently hidden, queue the request for later
            if (document.hidden) {
                return new Promise((resolve, reject) => {
                    failedRequestsQueue.push({
                        config: originalRequest,
                        resolve,
                        reject
                    });
                });
            }

            // If tab is active, try immediate retry with exponential backoff
            if (!originalRequest._retryCount) {
                originalRequest._retryCount = 0;
            }

            if (originalRequest._retryCount < 3) {
                originalRequest._retryCount++;

                // Wait before retry with exponential backoff
                const delay = 1000 * originalRequest._retryCount;
                await new Promise(resolve => setTimeout(resolve, delay));

                console.log(`Retrying request (attempt ${originalRequest._retryCount})...`);
                return api(originalRequest);
            }
        }

        // Show alert for API failures (only if not retrying or if it's a final failure)
        // Skip alerts for token refresh errors and retry attempts
        if (!isTokenRefreshError) {
            if (originalRequest && (!originalRequest._retryCount || originalRequest._retryCount >= 3)) {
                showApiFailureAlert(error, originalRequest);
            } else if (!originalRequest) {
                // Handle errors without config (e.g., network errors, request setup errors)
                showApiFailureAlert(error, null);
            }
        }

        return Promise.reject(error);
    }
);

// Wrapper functions for API calls
export const apiCall = {
    get: async (url, config = {}) => {
        try {
            const response = await api.get(url, config);
            return response.data;
        } catch (error) {
            // Alert is shown in interceptor, just re-throw
            throw error;
        }
    },

    post: async (url, data = {}, config = {}) => {
        try {
            const response = await api.post(url, data, config);
            return response.data;
        } catch (error) {
            // Alert is shown in interceptor, just re-throw
            throw error;
        }
    },

    put: async (url, data = {}, config = {}) => {
        try {
            const response = await api.put(url, data, config);
            return response.data;
        } catch (error) {
            // Alert is shown in interceptor, just re-throw
            throw error;
        }
    },

    delete: async (url, config = {}) => {
        try {
            const response = await api.delete(url, config);
            return response.data;
        } catch (error) {
            // Alert is shown in interceptor, just re-throw
            throw error;
        }
    }
};

export default api;
export { retryFailedRequests };

// Utility function to send authentication data via postMessage
export const sendAuthDataViaPostMessage = (authData) => {
    try {
        // Check if we're in an iframe or have a parent window
        if (window.parent && window.parent !== window) {
            window.parent.postMessage({
                type: 'AUTH_SUCCESS',
                data: {
                    access_token: authData.access_token,
                    refresh_token: authData.refresh_token,
                    user_id: authData.user_id || null,
                    email: authData.email || null,
                    timestamp: new Date().toISOString()
                }
            }, '*');
        } else {
            console.log('Not in iframe context, postMessage not sent');
        }
    } catch (error) {
        console.error('Error sending auth data via postMessage:', error);
    }
};